import { useState, useEffect, useContext, useRef } from "react";
import { Film, Maximize, Minimize, List, Lock, Play, ExternalLink, X, Move } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext, API } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";
import SyncedYouTubePlayer from "./SyncedYouTubePlayer";

// Draggable Movie Window Component
const DraggableMovieWindow = ({ video, onClose, onEnd, isAdmin, roomId, initialTime, initialPlaying }) => {
  const [size, setSize] = useState("medium"); // small, medium, large
  
  const sizes = {
    small: { width: 400, height: 280 },
    medium: { width: 640, height: 400 },
    large: { width: 900, height: 550 }
  };

  return (
    <DraggableResizableWindow
      title={
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4" />
          <span className="truncate max-w-[200px]">{video.title}</span>
        </div>
      }
      onClose={onClose}
      initialPosition={{ x: 100, y: 100 }}
      initialSize={sizes[size]}
      minSize={{ width: 320, height: 220 }}
      maxSize={{ width: 1200, height: 800 }}
      zIndex={6000}
      titleBarColor="bg-gradient-to-r from-red-800 to-red-900"
    >
      <div className="h-full flex flex-col bg-black">
        {/* Size controls */}
        <div className="bg-gray-900 px-2 py-1 flex items-center justify-between">
          <div className="flex gap-1">
            <button
              onClick={() => setSize("small")}
              className={`px-2 py-0.5 text-xs rounded ${size === "small" ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`}
            >
              Small
            </button>
            <button
              onClick={() => setSize("medium")}
              className={`px-2 py-0.5 text-xs rounded ${size === "medium" ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`}
            >
              Medium
            </button>
            <button
              onClick={() => setSize("large")}
              className={`px-2 py-0.5 text-xs rounded ${size === "large" ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`}
            >
              Large
            </button>
          </div>
          {isAdmin && (
            <button
              onClick={onEnd}
              className="px-2 py-0.5 text-xs bg-red-600 hover:bg-red-500 text-white rounded"
            >
              End Movie
            </button>
          )}
        </div>
        
        {/* Synced player */}
        <div className="flex-1 relative">
          <SyncedYouTubePlayer
            roomId={roomId}
            videoId={video.id}
            initialTime={initialTime}
            initialPlaying={initialPlaying}
            onEnded={onEnd}
          />
        </div>
        
        {/* Now Playing info */}
        <div className="bg-gradient-to-t from-black to-transparent p-2">
          <p className="text-white text-sm font-bold truncate">{video.title}</p>
          <p className="text-gray-400 text-xs">🎬 Watch party — anyone can pause / seek</p>
        </div>
      </div>
    </DraggableResizableWindow>
  );
};

const MovieRoomPanel = ({ roomId, isAdmin }) => {
  const { token, user } = useContext(AuthContext);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isPopout, setIsPopout] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestionTitle, setSuggestionTitle] = useState("");
  const [suggestionUrl, setSuggestionUrl] = useState("");
  const [suggestionNotes, setSuggestionNotes] = useState("");
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  // Sync state — initial playhead for late-joiners + tracks remote actions
  const [syncInitialTime, setSyncInitialTime] = useState(0);
  const [syncInitialPlaying, setSyncInitialPlaying] = useState(true);

  // Handle movie suggestion submission
  const handleSuggestMovie = async (e) => {
    e.preventDefault();
    if (!suggestionTitle.trim()) return;
    
    setSubmittingSuggestion(true);
    try {
      await axios.post(`${API}/movies/suggest`, {
        title: suggestionTitle.trim(),
        youtube_url: suggestionUrl.trim() || null,
        notes: suggestionNotes.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Movie suggestion submitted! Thanks for your input!");
      setShowSuggestModal(false);
      setSuggestionTitle("");
      setSuggestionUrl("");
      setSuggestionNotes("");
    } catch (error) {
      console.error("Failed to submit suggestion:", error);
      toast.error("Failed to submit suggestion. Please try again.");
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  // Fetch playlists from API
  useEffect(() => {
    const fetchPlaylists = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API}/movie-playlists`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPlaylists(response.data.playlists || []);
      } catch (error) {
        console.error("Failed to fetch playlists:", error);
        // Use fallback playlists if API fails
        setPlaylists([
          {
            playlist_id: "default-1",
            name: "🔥 MR_FUKERS TOP MOVIES",
            videos: [],
            video_count: 0
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchPlaylists();
  }, [token]);

  // Handle fullscreen
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error("Fullscreen error:", err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // On mount / room change: fetch current sync state so late-joiners jump in at the
  // current playhead instead of from the beginning.
  useEffect(() => {
    if (!token || !roomId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/movie-sync/${roomId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled || !res.data?.active) return;
        setCurrentVideo({ id: res.data.video_id, title: res.data.video_title || "Movie" });
        setSyncInitialTime(res.data.current_time || 0);
        setSyncInitialPlaying(!!res.data.is_playing);
        setIsPlaying(true);
        setShowPlaylist(false);
      } catch (_e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [token, roomId]);

  // Listen for room-wide movie state changes (e.g., someone else starts a movie).
  useEffect(() => {
    const onState = (ev) => {
      const data = ev.detail;
      if (!data || data.room_id !== roomId) return;
      if (data.actor && data.actor === user?.username) return; // our own action
      if (data.action === "end" || data.active === false) {
        setCurrentVideo(null);
        setIsPlaying(false);
        setShowPlaylist(true);
        toast.info(`${data.actor || "Someone"} ended the movie`);
        return;
      }
      if (data.action === "play" && data.video_id) {
        // If a different video, swap to it; otherwise just resume.
        if (!currentVideo || currentVideo.id !== data.video_id) {
          setCurrentVideo({ id: data.video_id, title: data.video_title || "Movie" });
          setSyncInitialTime(data.current_time || 0);
          setSyncInitialPlaying(true);
          setIsPlaying(true);
          setShowPlaylist(false);
          toast.info(`${data.actor || "Someone"} started: ${data.video_title || "a movie"}`);
        }
      }
    };
    window.addEventListener("movie:state", onState);
    return () => window.removeEventListener("movie:state", onState);
  }, [roomId, user?.username, currentVideo]);

  // Start playing a video — push to shared room sync state
  const startVideo = async (video) => {
    setCurrentVideo(video);
    setIsPlaying(true);
    setShowPlaylist(false);
    setSyncInitialTime(0);
    setSyncInitialPlaying(true);
    toast.info(`Now playing for the whole room: ${video.title}`);

    // Push to the sync state (broadcasts to other viewers via WS)
    try {
      await axios.post(`${API}/movie-sync/${roomId || "movie-theater"}/play`, {
        video_id: video.id,
        video_title: video.title,
        time: 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      console.error("Failed to start synced movie", err);
    }

    // Legacy "now playing" tracker (kept for admin dashboards)
    try {
      await axios.post(`${API}/movie/start`, {
        video_id: video.id,
        video_title: video.title,
        room_id: roomId || "movie-theater"
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_e) { /* non-fatal */ }
  };

  // End the movie for the whole room
  const handleVideoEnd = async () => {
    setIsPlaying(false);
    setCurrentVideo(null);
    setShowPlaylist(true);
    toast.success("Movie ended! You can now select another movie.");

    try {
      await axios.post(`${API}/movie-sync/${roomId || "movie-theater"}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_e) { /* non-fatal */ }
    try {
      await axios.post(`${API}/movie/stop`, {
        room_id: roomId || "movie-theater"
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_e) { /* non-fatal */ }
  };

  // Select a playlist
  const selectPlaylist = (playlist) => {
    setSelectedPlaylist(playlist);
  };

  return (
    <div ref={containerRef} className="h-full flex flex-col bg-gray-900 rounded-lg overflow-hidden" data-testid="movie-room-panel">
      {/* Draggable Movie Window (when popped out) */}
      {isPopout && currentVideo && (
        <DraggableMovieWindow 
          video={currentVideo}
          onClose={() => setIsPopout(false)}
          onEnd={handleVideoEnd}
          isAdmin={isAdmin}
          roomId={roomId || "movie-theater"}
          initialTime={syncInitialTime}
          initialPlaying={syncInitialPlaying}
        />
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-red-800 to-red-900 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Film className="w-5 h-5 text-white" />
          <span className="text-white font-bold">🎬 MOVIE THEATER</span>
        </div>
        <div className="flex items-center gap-2">
          {isPlaying && (
            <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded flex items-center gap-1">
              <Lock className="w-3 h-3" /> PLAYING
            </span>
          )}
          {isPlaying && !isPopout && (
            <button
              onClick={() => setIsPopout(true)}
              className="p-1.5 rounded bg-purple-600 text-white hover:bg-purple-500"
              title="Pop-out Movie (Draggable)"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded bg-white/20 text-white hover:bg-white/30"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
          {!isPlaying && (
            <button
              onClick={() => setShowPlaylist(!showPlaylist)}
              className={`p-1.5 rounded ${showPlaylist ? 'bg-white/20' : 'bg-transparent'} text-white hover:bg-white/30`}
            >
              <List className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video Player */}
        <div className={`flex-1 flex flex-col ${showPlaylist && !isPlaying ? 'w-2/3' : 'w-full'}`}>
          {/* Video Container */}
          <div className="flex-1 bg-black flex items-center justify-center relative">
            {currentVideo && isPlaying ? (
              <>
                {/* Synchronized YouTube player — anyone in the room can play / pause / seek */}
                <SyncedYouTubePlayer
                  roomId={roomId || "movie-theater"}
                  videoId={currentVideo.id}
                  initialTime={syncInitialTime}
                  initialPlaying={syncInitialPlaying}
                  onEnded={handleVideoEnd}
                />
                {/* Now Playing info */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none">
                  <div className="flex items-center justify-between pointer-events-auto">
                    <div>
                      <p className="text-white font-bold">{currentVideo.title}</p>
                      <p className="text-gray-300 text-xs">🎬 Watch party — anyone can pause, seek, or react</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={toggleFullscreen}
                        className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-white text-sm flex items-center gap-1"
                      >
                        {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                        {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={handleVideoEnd}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded text-white text-sm"
                          data-testid="end-movie-admin-btn"
                        >
                          End Movie (Admin)
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500">
                <Film className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Welcome to the Movie Theater</p>
                <p className="text-sm">Select a playlist and pick a movie to watch</p>
                <p className="text-xs mt-2 text-gray-600">🎬 Watch party — everyone watches in sync; anyone can pause / seek / react</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Playlist Selection (only visible when not playing) */}
        {showPlaylist && !isPlaying && (
          <div className="w-1/3 bg-gray-800 border-l border-gray-700 flex flex-col">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                Loading playlists...
              </div>
            ) : (
              <>
                {/* Playlist Selector */}
                <div className="p-3 border-b border-gray-700">
                  <label className="text-xs text-gray-400 mb-1 block">Select Playlist:</label>
                  <select
                    value={selectedPlaylist?.playlist_id || ""}
                    onChange={(e) => {
                      const pl = playlists.find(p => p.playlist_id === e.target.value);
                      selectPlaylist(pl);
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  >
                    <option value="">-- Choose a playlist --</option>
                    {playlists.map(pl => (
                      <option key={pl.playlist_id} value={pl.playlist_id}>
                        {pl.name} ({pl.videos?.length || 0} movies)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Movies in Selected Playlist (Top 3 only) */}
                <div className="flex-1 overflow-y-auto">
                  {selectedPlaylist ? (
                    selectedPlaylist.videos && selectedPlaylist.videos.length > 0 ? (
                      <div className="divide-y divide-gray-700">
                        {selectedPlaylist.videos.map((video, index) => (
                          <div
                            key={`${video.id}-${index}`}
                            className="p-3 hover:bg-gray-700 cursor-pointer transition-colors"
                            onClick={() => startVideo(video)}
                          >
                            <div className="flex items-center gap-3">
                              {/* Thumbnail */}
                              <div className="relative flex-shrink-0">
                                <img
                                  src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                                  alt=""
                                  className="w-24 h-14 object-cover rounded"
                                />
                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded opacity-0 hover:opacity-100 transition-opacity">
                                  <Play className="w-8 h-8 text-white" />
                                </div>
                              </div>
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">{video.title}</p>
                                <p className="text-gray-400 text-xs mt-1">Click to start watching</p>
                              </div>
                              {/* Play indicator */}
                              <div className="text-green-500">
                                <Play className="w-5 h-5" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 p-4">
                        <Film className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No movies in this playlist yet</p>
                        <p className="text-xs mt-1">Admin needs to add movies</p>
                      </div>
                    )
                  ) : (
                    <div className="text-center text-gray-500 p-4">
                      <List className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Select a playlist above</p>
                      <p className="text-xs mt-1">Then pick a movie to watch</p>
                    </div>
                  )}
                </div>

                {/* Info Footer */}
                <div className="p-3 bg-gray-900 border-t border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Film className="w-3 h-3" />
                      <span>🎬 Synced watch party — anyone can pause/seek</span>
                    </div>
                    <button
                      onClick={() => setShowSuggestModal(true)}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 rounded text-white text-xs font-bold"
                    >
                      💡 Suggest a Movie
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Movie Suggestion Modal */}
        {showSuggestModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowSuggestModal(false)}>
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg">💡 Suggest a Movie</h3>
                <button onClick={() => setShowSuggestModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSuggestMovie} className="space-y-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Movie Title *</label>
                  <input
                    type="text"
                    value={suggestionTitle}
                    onChange={(e) => setSuggestionTitle(e.target.value)}
                    placeholder="Enter movie name..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm mb-1">YouTube URL (optional)</label>
                  <input
                    type="url"
                    value={suggestionUrl}
                    onChange={(e) => setSuggestionUrl(e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Notes (optional)</label>
                  <textarea
                    value={suggestionNotes}
                    onChange={(e) => setSuggestionNotes(e.target.value)}
                    placeholder="Why do you want this movie?"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-sm h-20 resize-none"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowSuggestModal(false)}
                    className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 rounded text-white font-bold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!suggestionTitle.trim() || submittingSuggestion}
                    className="flex-1 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 rounded text-white font-bold"
                  >
                    {submittingSuggestion ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovieRoomPanel;
